import {
    WHATSAPP_CHANNEL,
    MESSENGER_CHANNEL,
    API_TWILIO,
    API_WSP,
} from 'src/constants/conversations';
import { TDataSendMessage } from '../components/types';
import { FromHandlerFactory } from '../factories/getFromByChannelFactory';
import { OUTCOME_DIRECTION } from 'src/constants/conversations';
import { TFormatMessageToSend, TResponseFormatMessageToSend } from './types';

/**
 * Formats a message object to be sent through the appropriate messaging channel (e.g., WhatsApp or Messenger).
 *
 * This function handles validation of message content, determines the correct "from" and "to" information based
 * on the conversation's direction and channel, and constructs a payload object ready for dispatch.
 *
 * If the message is empty and has no attachments or documentation, it returns an error.
 * Also returns an error if the sending source (`from`) could not be resolved.
 *
 * @param {TFormatMessageToSend} params - The parameters required to format the message.
 * @param {string} params.message - The message text to send.
 * @param {File[]} [params.attachments] - Optional files to send with the message.
 * @param {TDocumentation[]} [params.documentation] - Optional documentation to include.
 * @param {IConversation} params.conversation - The conversation object containing context like direction, area, etc.
 * @param {IUser} params.user - The user object representing the sender.
 *
 * @returns {TResponseFormatMessageToSend} An object containing either the formatted message data and send type,
 * or an error string indicating what went wrong.
 */

export const formatMessageToSend = ({
    message,
    attachments,
    documentation,
    conversation,
    presentationCard,
    user,
}: TFormatMessageToSend): TResponseFormatMessageToSend => {
    if (
        message.replace(/ /, '').trim() === '' &&
        !attachments &&
        (!documentation || documentation.length === 0)
    )
        return { error: 'Chats.emptyMessage' };

    let to = '';
    let from = '';

    const channel = conversation.channel;

    let response;

    const fromHandler = FromHandlerFactory.create(channel);

    response = fromHandler?.getChannelData(conversation);

    from = response?.from || '';

    if (!from) {
        return { error: 'Chats.errorPhone' };
    }

    if (conversation.direction === OUTCOME_DIRECTION) {
        if (conversation.to === from) to = conversation.from;
        else to = conversation.to;
    } else {
        if (conversation.from === from) to = conversation.to;
        else to = conversation.from;
    }

    const data: TDataSendMessage = {
        to: to || '',
        from: from || '',
        body: message,
        by: { name: user.name, id: user._id },
        area: conversation.area || '',
        storeId: conversation.store._id || '',
        channel: channel || '',
    };

    if (attachments?.length) data.files = attachments;
    if (documentation?.length) data.documents = documentation;
    if (presentationCard) {
        data.MediaUrl0 = presentationCard;
        data.MediaContentType0 = 'image/' + presentationCard.split('.').pop();
    }

    let sendType:
        | typeof MESSENGER_CHANNEL
        | typeof API_TWILIO
        | typeof API_WSP = MESSENGER_CHANNEL;

    if (channel === WHATSAPP_CHANNEL) {
        sendType = response?.api === API_TWILIO ? API_TWILIO : API_WSP;
    }

    return { data, sendType };
};
